import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ui/molecules/Modal';
import { Radio } from '@ui/molecules/inputs';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
import { translate } from '@utils/i18n';
import api from '@utils/axios';
import { updateStatusesWorkOrdersPath, fetchFilteredIdsWorkOrdersPath } from 'routes';
import toast from 'react-hot-toast';

const namespace = 'features.work_orders.index.update_statuses_modal';

function UpdateStatusesModal({
  workOrderIds,
  workOrdersCount,
  disabled,
  workOrderStatusesForSelect,
  queryParams,
  onDataChanged
}) {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState('');
  const [workOrdersProcessed, setWorkOrdersProcessed] = useState(0);
  const shouldContinueRef = useRef(true);

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const clearForm = () => {
    shouldContinueRef.current = false;
    setStatus('');
    setWorkOrdersProcessed(0);
    toggleDialog();
  };

  const fetchIdsUrl = useMemo(() => fetchFilteredIdsWorkOrdersPath({ new: true }), []);

  const radioOptions = useMemo(
    () => workOrderStatusesForSelect.map((s) => ({ value: String(s.value), label: s.title })),
    [workOrderStatusesForSelect]
  );

  const onSubmit = () => {
    shouldContinueRef.current = true;

    const body = new FormData();
    body.append('work_order_status_id', status);

    if (!workOrderIds) {
      api
        .get(fetchIdsUrl, { params: queryParams })
        .then(({ data }) => {
          batchUpdate(body, data.ids_chunks.flat());
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
        });
    } else {
      workOrderIds.split(',').forEach((id) => body.append('ids[]', id));
      api
        .patch(updateStatusesWorkOrdersPath({ format: 'json' }), body, { params: queryParams })
        .then(() => {
          onDataChanged();
          toast.success(translate('success', { namespace, count: workOrdersCount }));
          clearForm();
        })
        .catch(({ response }) => {
          toast.error(response?.data?.error ?? translate('error', { namespace }));
        });
    }
  };

  const batchUpdate = (formData, ids) => {
    if (!shouldContinueRef.current) {
      console.log('Loop aborted by the user.');
      return;
    }

    if (!ids.length) {
      toast.success(translate('success', { namespace, count: workOrdersCount }));
      onDataChanged();
      clearForm();
      return;
    }

    const currentIds = ids.splice(0, 10);
    const body = new FormData();
    body.append('work_order_status_id', formData.get('work_order_status_id'));
    currentIds.forEach((id) => body.append('ids[]', id));

    api
      .patch(updateStatusesWorkOrdersPath({ format: 'json' }), body)
      .then(({ data }) => {
        setWorkOrdersProcessed((prev) => prev + data.work_order_ids.length);
        batchUpdate(formData, ids, data);
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error ?? translate('error', { namespace }));
        clearForm();
      });
  };

  return (
    <>
      <button type="button" tabIndex="0" className="qmb-control" disabled={disabled} onClick={toggleDialog}>
        <i className="fa-light fa-pen-to-square" />
        {translate('button', { namespace })}
      </button>

      <Modal isOpen={visible} onClose={clearForm} ariaLabelledBy="wo-update-status-title">
        <ModalHeader title={translate('title', { namespace })} titleId="wo-update-status-title" onClose={clearForm} />
        <ModalBody>
          <Radio name="status" options={radioOptions} value={status} onChange={setStatus} />
        </ModalBody>
        <ModalFooter startActions={<Text>{translate('selected', { namespace, count: workOrdersCount })}</Text>}>
          {workOrdersProcessed > 0 && (
            <Text>
              {translate('processing', { namespace, count: workOrdersCount })}: {workOrdersProcessed}/{workOrdersCount}
            </Text>
          )}
          <Button onClick={clearForm}>{translate('cancel', { namespace: 'generic' })}</Button>
          <Button variant="primary" onClick={onSubmit} disabled={!status}>
            {translate('update', { namespace: 'generic' })}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

UpdateStatusesModal.propTypes = {
  workOrderIds: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool.isRequired,
  queryParams: PropTypes.object.isRequired,
  workOrdersCount: PropTypes.number.isRequired,
  workOrderStatusesForSelect: PropTypes.arrayOf(PropTypes.object),
  onDataChanged: PropTypes.func.isRequired
};

UpdateStatusesModal.defaultProps = {
  workOrderStatusesForSelect: [],
  workOrderIds: null
};

export default UpdateStatusesModal;
